/**
 * Unified error fallback component with multiple display variants.
 * @module error/ErrorFallback
 */
import { Alert, Button, Collapse } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import classes from './ErrorFallback.module.css';
import { getErrorMessage } from './utils.ts';

type ErrorFallbackVariant = 'alert' | 'inline' | 'minimal';

export interface ErrorFallbackProps extends Partial<FallbackProps> {
  error: unknown;
  resetErrorBoundary?: () => void;
  title?: string;
  context?: string;
  variant?: ErrorFallbackVariant;
  showRetry?: boolean;
  showDetails?: boolean;
  retryAction?: ReactNode;
  className?: string;
}

/**
 * Unified error fallback component with three display variants:
 *
 * - `alert` (default): Full Mantine Alert with icon, optional retry button, optional technical details
 * - `inline`: Lightweight div structure for grid cells (red title, gray message)
 * - `minimal`: Plain text only, no container or styling
 *
 * @example Alert variant with retry
 * ```tsx
 * <ErrorFallback
 *   error={error}
 *   resetErrorBoundary={reset}
 *   title="Chart failed to render"
 *   context="Type: Line | Rows: 50"
 *   showRetry
 *   showDetails
 * />
 * ```
 *
 * @example Inline variant for grid cells
 * ```tsx
 * <ErrorFallback
 *   error={error}
 *   title="Widget Error"
 *   variant="inline"
 * />
 * ```
 *
 * @example Minimal variant
 * ```tsx
 * <ErrorFallback error={error} variant="minimal" />
 * ```
 *
 * @example Custom retry action
 * ```tsx
 * <ErrorFallback
 *   error={error}
 *   retryAction={<ActionBarPrimitive.Reload />}
 * />
 * ```
 */
export function ErrorFallback({
  error,
  resetErrorBoundary,
  title,
  context,
  variant = 'alert',
  showRetry = false,
  showDetails = false,
  retryAction,
  className,
}: ErrorFallbackProps) {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const resolvedTitle = title ?? t('error');
  const errorMessage = getErrorMessage(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  if (variant === 'minimal') {
    return <span className={className}>{errorMessage}</span>;
  }

  if (variant === 'inline') {
    return (
      <div className={clsx(classes.inline, className)}>
        <div className={classes.inlineTitle}>{resolvedTitle}</div>
        <div className={classes.inlineMessage}>{errorMessage}</div>
      </div>
    );
  }

  return (
    <Alert
      className={className}
      color="red"
      icon={<IconAlertTriangle size={20} />}
      radius="md"
      title={resolvedTitle}
      variant="light"
    >
      {context && (
        <div style={{ marginBottom: 'var(--mantine-spacing-xs)', fontSize: 'var(--mantine-font-size-sm)' }}>
          {context}
        </div>
      )}
      <div style={{ color: 'var(--mantine-color-dimmed)', fontSize: 'var(--mantine-font-size-sm)' }}>
        {errorMessage}
      </div>
      {showDetails && errorStack && (
        <>
          <button className={classes.detailsToggle} onClick={() => setDetailsOpen(!detailsOpen)} type="button">
            {detailsOpen ? t('hideDetails') : t('showDetails')}
          </button>
          <Collapse expanded={detailsOpen}>
            <pre className={classes.detailsContent}>{errorStack}</pre>
          </Collapse>
        </>
      )}
      {showRetry && (
        <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
          {retryAction ?? (
            <Button leftSection={<IconRefresh size={16} />} onClick={resetErrorBoundary} size="sm" variant="light">
              {t('retry')}
            </Button>
          )}
        </div>
      )}
    </Alert>
  );
}
